import {
  endOfDay,
  format as dateFnsFormat,
  formatDistanceToNow,
  formatISO,
  getDay,
  getWeekOfMonth,
  parse,
  parseJSON,
  set,
  startOfDay,
  startOfMonth,
} from "date-fns";
import { RRule, Weekday } from "rrule";

function isValidDate(d: Date): boolean {
  return !isNaN(d.getTime());
}

/**
 * Compares two dates if they have the same exact UTC timestamp.
 */
export function isEqualDate(dateA: Date, dateB: Date): boolean {
  return dateA.getTime() === dateB.getTime();
}

/**
 * Compares two dates if they have the same year, month and day.
 */
export function isSameDate(dateA: Date, dateB: Date): boolean {
  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
}

/**
 * Compares two dates if they have the same hour, minute and optionally second.
 */
export function isSameTimeOfDay(
  timeA: Date,
  timeB: Date,
  compareSeconds = false
): boolean {
  return (
    timeA.getHours() === timeB.getHours() &&
    timeA.getMinutes() === timeB.getMinutes() &&
    (compareSeconds ? timeA.getSeconds() === timeB.getSeconds() : true)
  );
}

/**
 *
 */
export function format(
  input:
    | { datetime: Date | string | null | undefined }
    | { date: Date | string | null | undefined }
    | { timeofday: Date | string | null | undefined },
  format: string
): string {
  let dateObject;

  if ("datetime" in input) {
    dateObject = parseDateTime(input.datetime ?? new Date());
  } else if ("date" in input) {
    dateObject = parseDate(input.date ?? new Date());
  } else if ("timeofday" in input) {
    dateObject = parseTimeOfDay(input.timeofday ?? new Date());
  } else {
    throw new Error("Invalid input.");
  }

  return dateFnsFormat(dateObject, format);
}

/**
 * Formats a date/datetime into a humanized relative string, e.g.
 * "5 minutes ago" or "about 2 hours ago".
 */
export function humanizeDate(date: Date | string): string {
  const parsed = date instanceof Date ? date : parseDateTime(date);
  return formatDistanceToNow(parsed, { addSuffix: true });
}

/**
 * Formats `date` into ISO8601 date without time.
 */
export function formatISODate(date: Date): string {
  return formatISO(date, { representation: "date" });
}

/**
 * Formats `date` into ISO8601 date and time.
 */
export function formatISODateTime(date: Date): string {
  return formatISO(date);
}

/**
 * Formats `date` into ISO8601 time without timezone.
 */
export function formatISOTimeOfDay(date: Date): string {
  return dateFnsFormat(date, "HH:mm:ss");
}

/**
 * Creates a Date object with given time component values. Date component is the
 * current date. To be used for API attributes expecting time of day.
 */
export function newTimeOfDay(hour = 0, minute = 0, second = 0): Date {
  return set(new Date(), { hours: hour, minutes: minute, seconds: second });
}

/**
 * Parse date string from API to Date object. Time component of resulting Date
 * object is in 00:00:00 or 23:59:59 depending on second argument, defaults to
 * 00:00:00.
 */
export function parseDate(
  date: Date | string,
  time: "start" | "end" = "start"
): Date {
  if (date instanceof Date) {
    return time === "start" ? startOfDay(date) : endOfDay(date);
  }

  const missingTime =
    time === "start" ? startOfDay(new Date()) : endOfDay(new Date());
  return parse(date, "yyyy-MM-dd", missingTime);
}

/**
 * Parse date time string from API.
 */
export function parseDateTime(dateTime: Date | string): Date {
  return parseJSON(dateTime);
}

/**
 * Parse time string from API.
 * @param time
 * @returns
 */
export function parseTimeOfDay(
  time: Date | string,
  missingDate: Date = startOfDay(new Date())
): Date {
  if (time instanceof Date) {
    return time;
  }

  const withMilli = parse(time, "HH:mm:ss.SSSX", missingDate);
  if (isValidDate(withMilli)) return withMilli;

  const justSeconds = parse(time, "HH:mm:ssX", missingDate);
  if (isValidDate(justSeconds)) return justSeconds;

  const noTimezone = parse(time, "HH:mm:ss", missingDate);
  if (isValidDate(noTimezone)) return noTimezone;

  const noSeconds = parse(time, "HH:mm", missingDate);
  if (isValidDate(noSeconds)) return noSeconds;

  throw new Error(`${time} can't be parsed to a Date.`);
}

/**
 * Parse separate date string and time of day string into one Date object.
 * @param date
 * @param timeOfDay
 */
export function parseDateAndTime(
  date: Date | string,
  timeOfDay: Date | string
): Date {
  return parseTimeOfDay(timeOfDay, parseDate(date));
}

/**
 * Returns the ordinal number of a day of week in a month.
 *
 * Example:
 * ```
 * // Dec 1, 2022 => 1st Thursday
 * getNthDayOfMonth(new Date(2022, 12, 1)) // returns 1
 *
 * // Dec 9, 2022 => 2nd Friday
 * getNthDayOfMonth(new Date(2022, 12, 9)) // returns 2
 *
 * // Dec 12, 2022 => 2nd Monday
 * getNthDayOfMonth(new Date(2022, 12, 12)) // returns 2
 * ```
 */
export function getNthDayOfWeek(date: Date): number {
  const weekNum = getWeekOfMonth(date);
  const isBeforeFirstDayOfMonth = getDay(date) < getDay(startOfMonth(date));
  return isBeforeFirstDayOfMonth ? weekNum - 1 : weekNum;
}

const weekdayMap = {
  0: RRule.SU,
  1: RRule.MO,
  2: RRule.TU,
  3: RRule.WE,
  4: RRule.TH,
  5: RRule.FR,
  6: RRule.SA,
};
export function weekday(date: Date): Weekday {
  return weekdayMap[date.getDay() as keyof typeof weekdayMap];
}

export function month(date: Date): number {
  return date.getMonth() + 1;
}
export function monthday(date: Date): number {
  return date.getDate();
}
